import { useMemo, useRef, useState } from "react";
import { useTenantOrganization, useTenantRoles } from "../../../lib/api";
import { policyLabel } from "../../../lib/access";
import { emptyRole } from "../../../lib/organization";
import { Btn, Page, PageHead, cx } from "../../../components/msd/ui";
import RolePane from "./RolePane";
import { OrgLoading, PANES, PANE_BODY, PANE_NAV, plural } from "./kit";

const NEW = "__new";

/** Who holds each role, for the role's People section — only read for a login
 *  that may see People, so this page names nobody that page would not. */
function WithHolders({ tenantId, children }) {
  const { data } = useTenantOrganization(tenantId);
  const byRole = useMemo(() => {
    const m = new Map();
    for (const p of data?.people || []) {
      if (!m.has(p.role)) m.set(p.role, []);
      m.get(p.role).push(p);
    }
    return m;
  }, [data]);
  return children(byRole);
}

/**
 * Roles and access: the brand's roles listed on the left, and the chosen one on
 * the right — who it can see, which features it opens, and who holds it — edited
 * in place. Same data and writes as Super Admin's Roles editor.
 */
export default function RolesPage({ canEdit, canSeePeople, tenantId = null }) {
  if (canSeePeople)
    return (
      <WithHolders tenantId={tenantId}>
        {(byRole) => (
          <Roles canEdit={canEdit} byRole={byRole} tenantId={tenantId} />
        )}
      </WithHolders>
    );
  return <Roles canEdit={canEdit} byRole={null} tenantId={tenantId} />;
}

function Roles({ canEdit, byRole, tenantId }) {
  const { data, isLoading } = useTenantRoles(tenantId);
  const [chosen, setChosen] = useState(null);
  const [newName, setNewName] = useState("");
  const dirtyRef = useRef(false);
  const paneRef = useRef(null);
  const roles = data?.rows || [];

  if (isLoading) return <OrgLoading />;

  const people = roles.reduce((n, r) => n + (r.user_count || 0), 0);
  const current =
    chosen === NEW ? null : roles.find((r) => r.code === chosen) || roles[0];
  const shown = chosen === NEW ? emptyRole() : current;

  const choose = (code) => {
    if (code === (chosen === NEW ? NEW : current?.code)) return;
    // eslint-disable-next-line no-alert
    if (
      dirtyRef.current &&
      !window.confirm(
        "Leave without saving? Your changes to this role will be lost.",
      )
    )
      return;
    dirtyRef.current = false;
    setNewName("");
    setChosen(code);
    // Narrow screens stack the role under the list, so bring it into view.
    if (window.matchMedia?.("(max-width: 900px)").matches)
      paneRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <Page>
      <PageHead
        title="Roles and access"
        sub={`${plural(roles.length, "role", "roles")} · ${plural(people, "member", "members")}`}
      >
        {canEdit && (
          <Btn variant="pri" icon="plus2" onClick={() => choose(NEW)}>
            New role
          </Btn>
        )}
      </PageHead>

      {/* The two panes fill the window below the title and scroll on their own, so the
          title, the list and the role's Save all stay in view. Narrow screens stack them. */}
      <div className={PANES}>
        <nav aria-label="Roles" className={PANE_NAV}>
          <ul className="m-0 flex list-none flex-col gap-1 p-0">
            {roles.map((r) => {
              const on = shown && r.code === shown.code && chosen !== NEW;
              return (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => choose(r.code)}
                    aria-current={on ? "true" : undefined}
                    className={cx(
                      "flex w-full cursor-pointer items-center gap-3 rounded-[12px] border-0 px-3 py-[10px] text-left text-inherit",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3A5F94]",
                      on ? "bg-msd-soft" : "bg-transparent hover:bg-msd-ground",
                    )}
                  >
                    <span className="min-w-0 grow">
                      <span
                        className={cx(
                          "block truncate text-[14.5px] text-msd-ink",
                          on ? "font-bold" : "font-semibold",
                        )}
                      >
                        {r.label}
                      </span>
                      <span className="block truncate text-[12.5px] text-msd-muted">
                        {policyLabel(r.access_policy)} ·{" "}
                        {plural(r.user_count || 0, "member", "members")}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
            {chosen === NEW && (
              <li>
                <span
                  aria-current="true"
                  className="flex rounded-[12px] bg-msd-soft px-3 py-[10px] text-[14.5px] font-bold text-msd-ink"
                >
                  <span className="min-w-0 grow">
                    <span className="block truncate">
                      {newName.trim() || "Untitled role"}
                    </span>
                    <span className="block text-[12.5px] font-normal text-msd-muted">
                      Not saved yet
                    </span>
                  </span>
                </span>
              </li>
            )}
          </ul>
        </nav>

        <div ref={paneRef} className={PANE_BODY}>
          {shown ? (
            <RolePane
              tenantId={tenantId}
              key={chosen === NEW ? NEW : shown.id}
              role={shown}
              people={
                byRole && chosen !== NEW ? byRole.get(shown.code) || [] : null
              }
              canEdit={canEdit}
              dirtyRef={dirtyRef}
              onName={setNewName}
              onDone={(code) => {
                dirtyRef.current = false;
                setNewName("");
                setChosen(code);
              }}
            />
          ) : (
            <p className="m-0 px-6 py-10 text-center text-[14px] text-msd-muted">
              No roles yet.
            </p>
          )}
        </div>
      </div>
    </Page>
  );
}
